import type { ThumbnailsConfig } from '../types'

export interface ThumbFrame {
  url: string
  x: number
  y: number
  w: number
  h: number
}

function isSprite(t: ThumbnailsConfig): t is Extract<ThumbnailsConfig, { spriteUrl: string }> {
  return 'spriteUrl' in t
}

/** Resolve thumbnail frame for a given media time */
export function thumbAt(config: ThumbnailsConfig | undefined, time: number): ThumbFrame | null {
  if (!config) return null
  if (isSprite(config)) {
    const idx = Math.max(0, Math.floor(time / Math.max(0.1, config.interval)))
    const col = idx % config.columns
    const row = Math.floor(idx / config.columns)
    return {
      url: config.spriteUrl,
      x: col * config.width,
      y: row * config.height,
      w: config.width,
      h: config.height,
    }
  }
  return null
}

/** Parse WebVTT thumbnail file (sprite regions) — best-effort */
export async function loadVttThumbs(vttUrl: string): Promise<
  { start: number; end: number; frame: ThumbFrame }[]
> {
  const res = await fetch(vttUrl)
  if (!res.ok) return []
  const text = await res.text()
  const cues: { start: number; end: number; frame: ThumbFrame }[] = []
  const blocks = text.split(/\n\n+/)
  for (const block of blocks) {
    const lines = block.trim().split('\n')
    if (lines.length < 2) continue
    const timing = lines.find((l) => l.includes('-->'))
    if (!timing) continue
    const [a, b] = timing.split('-->').map((s) => s.trim())
    const start = vttTime(a)
    const end = vttTime(b)
    const payload = lines[lines.indexOf(timing) + 1] || ''
    const m = payload.match(/^(.*?)#xywh=(\d+),(\d+),(\d+),(\d+)/)
    if (!m) continue
    cues.push({
      start,
      end,
      frame: { url: m[1], x: +m[2], y: +m[3], w: +m[4], h: +m[5] },
    })
  }
  return cues
}

function vttTime(t: string): number {
  const clean = t.split(' ')[0]
  const parts = clean.split(':').map(Number)
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2]
  if (parts.length === 2) return parts[0] * 60 + parts[1]
  return Number(clean) || 0
}

export function thumbFromVtt(
  cues: { start: number; end: number; frame: ThumbFrame }[],
  time: number,
): ThumbFrame | null {
  const cue = cues.find((c) => time >= c.start && time < c.end) || cues[cues.length - 1]
  return cue?.frame ?? null
}
